/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { ReactNode } from 'react'

export interface MenuAction {
  id: string
  label: string
  icon?: ReactNode
  onClick: () => void
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  disabled?: boolean
  tooltip?: string
}

export interface MenuToggle {
  id: string
  label: string
  icon?: ReactNode
  value: string | boolean
  options?: { label: string; value: string | boolean }[]
  onChange: (value: string | boolean) => void
  tooltip?: string
}

interface PageMenuProps {
  actions?: MenuAction[]
  toggles?: MenuToggle[]
  className?: string
}

/**
 * Reusable page menu component for actions and toggles
 */
const PageMenu: React.FC<PageMenuProps> = ({ actions = [], toggles = [], className = '' }) => {
  const renderAction = (action: MenuAction) => {
    const buttonClass = `btn btn-${action.size || 'sm'} ${
      action.variant === 'outline'
        ? 'btn-outline'
        : action.variant === 'ghost'
          ? 'btn-ghost'
          : action.variant === 'secondary'
            ? 'btn-secondary'
            : action.variant === 'primary'
              ? 'btn-primary'
              : 'btn-outline'
    }`

    return (
      <button
        key={action.id}
        className={buttonClass}
        onClick={action.onClick}
        disabled={action.disabled}
        title={action.tooltip}
      >
        {action.icon && <span className="mr-1">{action.icon}</span>}
        {action.label}
      </button>
    )
  }

  const renderToggle = (toggle: MenuToggle) => {
    if (toggle.options && toggle.options.length > 1) {
      // Multi-option toggle (join buttons)
      return (
        <div key={toggle.id} className="join" title={toggle.tooltip}>
          {toggle.options.map((option) => (
            <button
              key={`${toggle.id}-${option.value}`}
              className={`btn btn-sm join-item ${
                toggle.value === option.value ? 'btn-active btn-primary' : 'btn-outline'
              }`}
              onClick={() => toggle.onChange(option.value)}
            >
              {toggle.icon && <span className="mr-1">{toggle.icon}</span>}
              {option.label}
            </button>
          ))}
        </div>
      )
    }

    // Simple toggle button
    return (
      <button
        key={toggle.id}
        className={`btn btn-sm ${toggle.value ? 'btn-primary' : 'btn-outline'}`}
        onClick={() => toggle.onChange(!toggle.value)}
        title={toggle.tooltip}
      >
        {toggle.icon && <span className="mr-1">{toggle.icon}</span>}
        {toggle.label}
      </button>
    )
  }

  return (
    <div className={`flex flex-col gap-2 items-center ${className}`}>
      {/* Toggles */}
      {toggles.map(renderToggle)}

      {/* Separator */}
      {actions.length > 0 && toggles.length > 0 && <div className="divider divider-horizontal mx-1 w-px bg-base-300" />}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">{actions.map(renderAction)}</div>
    </div>
  )
}

export default PageMenu
